import { useState } from 'react';

import { AutocompleteInputChangeReason } from '@mui/material';
import { SxProps } from '@mui/system';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import DevicesService from '~features/devices/services/devicesService';
import { BaseReference } from '~interfaces';
import DeviceType from '~interfaces/DeviceType';
import { getService } from '~services/serviceFactory';

import AsyncAutocomplete from './asyncAutocomplete';

const service = getService(DevicesService);

interface DeviceTypeDropdownProps {
	onValueChanged: (newValue?: DeviceType) => void;
	disableClearable?: boolean;
	sx?: SxProps;
}

const DevicesAutocomplete = ({ disableClearable = false, ...props }: DeviceTypeDropdownProps) => {
	const { t } = useTranslation();
	const [allDeviceTypes, setAllDeviceTypes] = useState([] as BaseReference[]);
	useSWR(['deviceTypes'], ([_]) => service.getDeviceTypes(), {
		keepPreviousData: true,
		revalidateFirstPage: false,
		onSuccess: (data) => {
			setAllDeviceTypes(data);
			setFilteredDeviceTypes(data);
		},
	});

	// Hooks and states
	const [filteredDeviceTypes, setFilteredDeviceTypes] = useState(allDeviceTypes);

	// Input handling
	const handleInputChange = (
		_e: React.SyntheticEvent,
		value: string,
		_reason: AutocompleteInputChangeReason
	) => {
		if (value.length <= 0) {
			setFilteredDeviceTypes(allDeviceTypes);
		} else {
			setFilteredDeviceTypes(
				allDeviceTypes.filter((deviceType) =>
					deviceType.name.toLowerCase().includes(value.toLowerCase())
				)
			);
		}
	};

	const handleOptionSelected = (value: BaseReference | undefined) => {
		props.onValueChanged(value as DeviceType | undefined);
	};

	return (
		<AsyncAutocomplete
			placeholder={t('device.labels.type', { ns: 'entities' })}
			noOptionsText={t('noResults', { ns: 'system' })}
			options={filteredDeviceTypes}
			onInputChange={handleInputChange}
			onOptionSelected={handleOptionSelected}
			debounce={false}
			enableInfiniteScroll={false}
			disableClearable={disableClearable}
			sx={{
				width: 200,
				...props.sx,
			}}
		/>
	);
};

export default DevicesAutocomplete;
